import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Grid, Divider, Checkbox } from "@mui/material";
import { Autocomplete, IconButton, TextField } from "@totalsoft/rocket-ui";
import CancelIcon from "@mui/icons-material/Cancel";
import { onCheckBoxChange } from "utils/propertyChangeAdapters";
import Autosuggest from "components/common/inputs/Autosuggest";
import { isValid, getErrors } from "@totalsoft/pure-validations-react";
import { withParams } from "providers/ParamsConsumer";
import PhoneNumberField from "components/common/PhoneNumberTextField";

export const ContactDataRowComponent = ({
  contactInfo,
  deleteContactInfo,
  onPropertyChange,
  positions,
  loadPersons,
  validation,
  loadPartnerDetails,
  idx,
  hasWritePermission,
  params
}) => {
  const { t } = useTranslation();

  const personValue = useMemo(() => {
    if (!contactInfo.personId) return null;
    return { id: contactInfo.personId, uniqueID: contactInfo.uniqueID };
  }, [contactInfo]);

  return (
    <Grid container spacing={1} key={contactInfo.contactInfoId} pt="2">
      <Grid item xs={12} md={3} sm={6}>
        <span> {t("Partner.PersonUniqueID")} </span>
        <div id={"contactData_" + idx + "_uniqueID"}>
          <Autosuggest
            value={personValue}
            onChange={onPropertyChange("personId", "uniqueID", loadPartnerDetails)}
            disabled={!hasWritePermission}
            isClearable={false}
            valueKey="id"
            labelKey="uniqueID"
            loadOptions={loadPersons}
            error={!isValid(validation.uniqueID)}
            helperText={getErrors(validation.uniqueID)}
          />
        </div>
      </Grid>
      <Grid item xs={12} md={3} sm={6}>
        <span> {t("Partner.PersonName")} </span>
        <TextField
          id={"contactData_" + idx + "_personName"}
          name={"personName"}
          fullWidth
          value={contactInfo.personName || ""}
          onChange={onPropertyChange("personName")}
          disabled={!hasWritePermission}
          error={!isValid(validation.personName)}
          helperText={getErrors(validation.personName)}
        />
      </Grid>
      <Grid item xs={12} md={3} sm={6}>
        <span> {t("Partner.Email")} </span>
        <TextField
          id={"contactData_" + idx + "_email"}
          name={"email"}
          fullWidth
          value={contactInfo.email || ""}
          onChange={onPropertyChange("email")}
          disabled={!hasWritePermission}
          error={!isValid(validation.email)}
          helperText={getErrors(validation.email)}
        />
      </Grid>
      <Grid item xs={12} md={3} sm={6}>
        <span> {t("Partner.MobilePhone")} </span>
        <PhoneNumberField
          defaultCountry={params.defaultCountryCode}
          country={contactInfo.phoneCountryCode}
          id={"contactData_" + idx + "_phone"}
          name={"phone"}
          fullWidth
          variant="standard"
          value={contactInfo.phone || ""}
          onChange={onPropertyChange("phone")}
          disabled={!hasWritePermission}
          error={!isValid(validation.phone)}
          helperText={getErrors(validation.phone)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <span> {t("Partner.Position")} </span>
        <Autocomplete
          id={"contactData_" + idx + "_position"}
          fullWidth
          value={positions.length > 0 ? contactInfo.positionId || "" : ""}
          labelKey="positionName"
          valueKey="positionId"
          simpleValue={true}
          onChange={onPropertyChange("positionId")}
          disabled={!hasWritePermission}
          options={positions}
          error={!isValid(validation.positionId)}
          helperText={getErrors(validation.positionId)}
        />
      </Grid>
      <Grid container xs={10} md={6} justifyContent={"space-between"} alignItems={"flex-end"}>
        <Grid item style={{ paddingLeft: 15 }}>
          <span>{t("Partner.Default")}</span>
          <Checkbox
            id={"contactData_" + idx + "_default"}
            name={"isDefaultContact"}
            checked={contactInfo.isDefaultContact || false}
            onChange={onCheckBoxChange(onPropertyChange("isDefaultContact"))}
            disabled={!hasWritePermission}
            color="primary"
          />
        </Grid>
        <Grid item style={{ paddingLeft: 15 }}>
          <span> {t("Partner.IsLegalRepresentative")} </span>
          <Checkbox
            id={"contactData_" + idx + "_isLegalRepresentative"}
            name={"isLegalRepresentative"}
            checked={contactInfo?.isLegalRepresentative || false}
            onChange={onCheckBoxChange(onPropertyChange("isLegalRepresentative"))}
            disabled={!hasWritePermission}
            color="primary"
          />
        </Grid>
        <Grid item style={{ paddingLeft: 15 }}>
          <span>{t("Partner.ESignature")}</span>
          <Checkbox
            id={"contactData_" + idx + "_isESignature"}
            name={"isESignature"}
            checked={(contactInfo && contactInfo.isESignature) || false}
            onChange={onCheckBoxChange(onPropertyChange("isESignature"))}
            disabled={!hasWritePermission}
            color="primary"
          />
        </Grid>
      </Grid>
      <Grid item xs={2} sx={{ textAlign: "end" }}>
        <IconButton
          id={"contactInfo_" + idx + "_deleteBtn"}
          color="primary"
          variant="text"
          onClick={() => deleteContactInfo()}
          disabled={!hasWritePermission}
        >
          <CancelIcon />
        </IconButton>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          id={"contactData_" + idx + "_comments"}
          label={t("Partner.Comments")}
          multiline
          inputProps={{ maxLength: 1000 }}
          rows={3}
          value={contactInfo.comments || ""}
          onChange={onPropertyChange("comments")}
          disabled={!hasWritePermission}
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} md={12} sm={12}>
        <Divider style={{ paddingBottom: "10px" }} />
      </Grid>
    </Grid>
  );
};

ContactDataRowComponent.propTypes = {
  deleteContactInfo: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  contactInfo: PropTypes.object.isRequired,
  positions: PropTypes.array.isRequired,
  loadPersons: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  loadPartnerDetails: PropTypes.func,
  idx: PropTypes.number,
  params: PropTypes.object,
  hasWritePermission: PropTypes.bool.isRequired
};

export default withParams(ContactDataRowComponent);
